import api from './base-api';
import {
	BulkCreateMatchesDto,
	BulkUpdateMatchesDto,
	CreateMatchDto,
	GetMoreMatchesQueryDto,
	PaginatedMoreMatchesResponseDto,
	RefreshMatchResultsDto,
	UpdateMatchDto,
	UpdateMatchOpeningDescriptionDto,
	latestMatchInfoDto,
	CreateMatchExclusionDto,
	MatchExclusionDto,
	MatchExclusionWithDisplayDto,
	RoundGroupDto,
	UpdateMatchStatusDto,
	UpdateMatchStatusResponse,
} from '@sportspredict/shared';

export const matchesApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getMoreMatchesByEvent: builder.query<PaginatedMoreMatchesResponseDto, GetMoreMatchesQueryDto>({
			query: ({ excludeMatchIds, ...params }) => ({
				url: 'matches/event/more-matches',
				params: {
					...params,
					excludeMatchIds: excludeMatchIds?.join(','),
				},
			}),
			providesTags: (_result, _error, { eventId, lobbyId, tab }) => [
				{
					type: 'Game',
					id: `${eventId}-${lobbyId ?? 'all'}-more-matches-${tab ?? 'auto'}`,
				},
			],
			extraOptions: { skipAuth: true },
		}),
		getLatestMatchInfo: builder.query<latestMatchInfoDto, { eventId: string }>({
			query: ({ eventId }) => ({
				url: `matches/latest-info/${eventId}`,
			}),
			extraOptions: { skipAuth: true },
		}),
		getLatestMatchIdByEventId: builder.query<{ id: string } | null, { eventId: string }>({
			query: ({ eventId }) => ({
				url: `matches/latest/${eventId}`,
			}),
			extraOptions: { skipAuth: true },
		}),
		refreshMatchResults: builder.mutation<{ success: boolean }, RefreshMatchResultsDto>({
			query: (data) => ({
				url: 'matches/refresh-results',
				method: 'POST',
				body: data,
			}),
		}),
		getRoundGroups: builder.query<RoundGroupDto[], string>({
			query: (eventId) => ({
				url: `matches/round-groups/${eventId}`,
				method: 'GET',
			}),
		}),
		createMatch: builder.mutation<{ id: string }, CreateMatchDto>({
			query: (data) => ({
				url: 'matches',
				method: 'POST',
				body: data,
			}),
			invalidatesTags: [{ type: 'Match', id: 'LIST' }],
		}),
		updateMatch: builder.mutation<void, UpdateMatchDto>({
			query: ({ id, ...rest }) => ({
				url: 'matches',
				method: 'PUT',
				body: { id, ...rest },
			}),
			invalidatesTags: ['Match'],
		}),
		deleteMatch: builder.mutation<void, string>({
			query: (id) => ({
				url: `matches/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Match'],
		}),
		bulkCreateMatches: builder.mutation<void, BulkCreateMatchesDto>({
			query: (body) => ({
				url: 'matches/all',
				method: 'POST',
				body,
			}),
			invalidatesTags: [{ type: 'Match', id: 'LIST' }],
		}),
		bulkUpdateMatches: builder.mutation<void, BulkUpdateMatchesDto>({
			query: (body) => ({
				url: 'matches/all',
				method: 'PATCH',
				body,
			}),
			invalidatesTags: ['Match'],
		}),
		updateMatchOpeningDescription: builder.mutation<void, UpdateMatchOpeningDescriptionDto>({
			query: (data) => ({
				url: 'matches/opening-description',
				method: 'PUT',
				body: data,
			}),
			invalidatesTags: ['Match'],
		}),
		getMatchExclusions: builder.query<MatchExclusionWithDisplayDto[], string>({
			query: (eventId) => ({
				url: `matches/exclusions/${eventId}`,
				method: 'GET',
			}),
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }) => ({ type: 'MatchExclusion' as const, id })),
							{ type: 'MatchExclusion' as const, id: 'LIST' },
						]
					: [{ type: 'MatchExclusion' as const, id: 'LIST' }],
		}),
		addMatchExclusion: builder.mutation<void, CreateMatchExclusionDto>({
			query: (body) => ({
				url: 'matches/exclusions',
				method: 'POST',
				body,
			}),
			invalidatesTags: [{ type: 'MatchExclusion', id: 'LIST' }],
		}),
		removeMatchExclusion: builder.mutation<void, string>({
			query: (id) => ({
				url: `matches/exclusions/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [{ type: 'MatchExclusion', id: 'LIST' }],
		}),
		bulkDeleteMatches: builder.mutation<void, { matchIds: string[] }>({
			query: (body) => ({
				url: 'matches/bulk-delete',
				method: 'POST',
				body,
			}),
		}),
		bulkAddMatchExclusions: builder.mutation<
			MatchExclusionDto[],
			{ exclusions: CreateMatchExclusionDto[] }
		>({
			query: (body) => ({
				url: 'matches/exclusions/bulk',
				method: 'POST',
				body,
			}),
			invalidatesTags: [{ type: 'MatchExclusion', id: 'LIST' }],
		}),
		updateMatchStatus: builder.mutation<UpdateMatchStatusResponse, UpdateMatchStatusDto>({
			query: (body) => ({
				url: 'matches/status',
				method: 'POST',
				body,
			}),
		}),
	}),
});

export const {
	useGetMoreMatchesByEventQuery,
	useLazyGetMoreMatchesByEventQuery,
	useGetLatestMatchInfoQuery,
	useGetLatestMatchIdByEventIdQuery,
	useRefreshMatchResultsMutation,
	useGetRoundGroupsQuery,
	useCreateMatchMutation,
	useUpdateMatchMutation,
	useDeleteMatchMutation,
	useBulkCreateMatchesMutation,
	useBulkUpdateMatchesMutation,
	useUpdateMatchOpeningDescriptionMutation,
	useGetMatchExclusionsQuery,
	useAddMatchExclusionMutation,
	useRemoveMatchExclusionMutation,
	useBulkDeleteMatchesMutation,
	useBulkAddMatchExclusionsMutation,
	useUpdateMatchStatusMutation,
} = matchesApi;
